import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import DOMPurify from "dompurify";
import { CalendarDays, User, ArrowLeft } from "lucide-react";

const SITE = "https://www.globalcompass.in";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [slug]);

  useEffect(() => {
    if (!post || post === false) return undefined;
    if (post.slug && slug !== post.slug) {
      navigate(`/blog/${post.slug}`, { replace: true });
      return undefined;
    }
    const prevTitle = document.title;
    document.title = post.metaTitle || `${post.title} | Global Compass Travel Blog`;
    const desc = (post.metaDescription && post.metaDescription.trim()) || (post.summary || "").slice(0, 160);
    const ogImg = post.ogImage ? fileUrl(post.ogImage) : (post.coverImage ? fileUrl(post.coverImage) : `${SITE}/logo.jpg`);
    const canonical = `${SITE}/blog/${post.slug || post.id}`;
    const restores = [];
    const apply = (tagName, idAttr, idVal, contentAttr, contentVal) => {
      let el = document.head.querySelector(`${tagName}[${idAttr}="${idVal}"]`);
      if (el) {
        const prev = el.getAttribute(contentAttr);
        restores.push(() => { if (prev === null) el.removeAttribute(contentAttr); else el.setAttribute(contentAttr, prev); });
      } else {
        el = document.createElement(tagName);
        el.setAttribute(idAttr, idVal);
        document.head.appendChild(el);
        restores.push(() => el.remove());
      }
      el.setAttribute(contentAttr, contentVal);
    };
    apply("meta", "name", "description", "content", desc);
    apply("link", "rel", "canonical", "href", canonical);
    apply("meta", "property", "og:title", "content", post.metaTitle || post.title);
    apply("meta", "property", "og:description", "content", desc);
    apply("meta", "property", "og:image", "content", ogImg);
    apply("meta", "property", "og:url", "content", canonical);
    apply("meta", "property", "og:type", "content", "article");

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-blog-ld", "true");
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: desc,
      image: ogImg,
      author: { "@type": "Organization", name: post.author || "Global Compass" },
      publisher: { "@type": "Organization", name: "Global Compass Tours & Travels", logo: { "@type": "ImageObject", url: `${SITE}/logo.jpg` } },
      datePublished: post.publishedAt || post.createdAt,
      dateModified: post.updatedAt || post.publishedAt || post.createdAt,
      mainEntityOfPage: canonical,
    });
    document.head.appendChild(ld);
    restores.push(() => ld.remove());

    return () => { document.title = prevTitle; restores.forEach((r) => r()); };
  }, [post, slug, navigate]);

  if (post === null) return <div className="p-20 text-center text-slate-500">Loading…</div>;
  if (post === false) return <div className="p-20 text-center text-slate-600">Post not found. <Link to="/blog" className="text-amber-700 underline">Back to the blog</Link></div>;

  return (
    <div className="bg-ivory">
      {post.coverImage && (
        <div className="relative h-[46vh] min-h-[320px] bg-maroon overflow-hidden">
          <img src={fileUrl(post.coverImage)} alt={post.title} className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
        </div>
      )}
      <article className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-900 mb-6 text-sm font-semibold" data-testid="back-to-blog">
          <ArrowLeft size={16} /> Back to the blog
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-maroon leading-tight" data-testid="blog-post-title">{post.title}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-5 text-slate-500 text-sm">
          <span className="inline-flex items-center gap-2"><CalendarDays size={16} /> {formatDate(post.publishedAt)}</span>
          <span className="inline-flex items-center gap-2"><User size={16} /> {post.author || "Global Compass"}</span>
        </div>
        {post.summary && <p className="mt-6 text-xl text-slate-700 leading-relaxed font-medium">{post.summary}</p>}
        <div className="mt-8 blog-content" data-testid="blog-post-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body || "") }} />
      </article>
    </div>
  );
}
